import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Item } from "../../../api/menu";
import { useMenuItems } from "../../../hooks/useMenu";
import { lunchUnitFor } from "../../../lib/lunchCatalog";
import {
  getItemName,
  getItemUnitAndSize,
} from "../../../utils/translations";

export interface LunchProductView {
  item: Item | null;
  name: string;
  mainPrice: number | null;
  mainUnit: string | null;
}

/**
 * Основният продукт зад ред от обедно меню (име, основна цена, размер) — от
 * каталога на „Маркиране". Страницата е в обхват "regular" (CatalogScopeContext),
 * така че тук са само истинските артикули/рецепти, без обедните копия.
 */
export const useLunchProducts = () => {
  const items = useMenuItems();
  const { t, i18n } = useTranslation();
  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items],
  );
  return useCallback(
    (itemId: number): LunchProductView => {
      const item = itemsById.get(itemId) ?? null;
      if (!item)
        return { item: null, name: t("lunchMenu.missingProduct"), mainPrice: null, mainUnit: null };
      return {
        item,
        name: getItemName(item.itemLangs, i18n.language, item.slug),
        mainPrice: item.price,
        mainUnit: getItemUnitAndSize(item.itemLangs, i18n.language),
      };
    },
    [itemsById, i18n.language, t],
  );
};

/** Размерът за показване: обедният за езика (ако е сменен), иначе на продукта. */
export const lunchUnitOf = (
  unitAndSizeLangs: Record<string, string> | null | undefined,
  language: string,
  mainUnit: string | null,
) => {
  const lunchUnit = lunchUnitFor(unitAndSizeLangs, language);
  return {
    text: lunchUnit || mainUnit,
    changed: !!lunchUnit && lunchUnit !== mainUnit,
  };
};
